import * as React from "react";
import { merge, $ } from "glamor";
import { Form } from "formsy-react";
import { formatMessage } from "lib/globalize-util";
import TextField from "components/form/text-field";
import debounce from "lodash/debounce";
import Sort from "components/sort-picker";
import { Map } from "immutable";
import { DATATEST } from "constants/integration";
import { CatalogActions } from "./product-collection-shop-catalog-search-actions";

interface IProps {
    resource: string;
    filters: Map<any, any>;
    filterOptions?: any;
    onChange: (filters) => void;
    linesheetsSort?: string;
    linesheetsSortOrder?: "asc" | "desc";
    location?: any;
    onSortChange?: (e, index, value) => void;
    viewport?: any;
    className?: string;
    onAddAllItems?: () => void;
    cacheKey?: any;
    bulkAddEnabled?: boolean;
    onResetToDefaultShopView?: () => void;
    onConfigureShopView?: () => void;
    selectedShopViewKey?: string;
}

export class SearchBrands extends React.PureComponent<IProps, any> {
    sortChoices = [
        ["created_on.desc", formatMessage("CREATED_NEWEST")],
        ["created_on.asc", formatMessage("CREATED_OLDEST")],
        ["modified_on.desc", formatMessage("UPDATED_NEWEST")],
        ["modified_on.asc", formatMessage("UPDATED_OLDEST")],
        ["title.asc", formatMessage("TITLE_A_Z")],
        ["title.desc", formatMessage("TITLE_Z_A")],
        ["custom.asc", formatMessage("Merchandised")],
    ];

    private styles = merge(
        {
            margin: "0 8px",
            height: "49px",
            lineHeight: "48px",
            display: "flex",
            flexWrap: "nowrap",
            paddingBottom: "16px",
        },
        $(" > form", {
            width: "100%",
        }),
        $(" > button", {
            padding: "12px 12px 12px 0px !important",
        }),
        $(" .spacer", {
            flexGrow: 1,
            flexShrink: 1,
            marginTop: "-2px",
            marginRight: "24px",
        }),
        $(" .sort", {
            marginLeft: "auto",
        }),
        $(" .sort > div > button", {
            padding: "12px 0px 12px 12px !important",
        })
    );

    private muiStyles = {
        textField: {
            height: "44px",
            display: "block",
        },
        linesheetActions: {
            width: "36px",
            padding: "0 0 12px 12px",
        },
    };

    constructor(props) {
        super(props);

        const { filters } = props;
        const searchTerm = filters ? (filters.get("__search") ? filters.get("__search") : "") : "";

        this.state = {
            searchTerm,
        };

        this.handleThrottleSearchChange = debounce(this.handleThrottleSearchChange, 500);
    }

    componentDidMount() {
        if (this.props.cacheKey && this.props.cacheKey.includes("__search")) {
            this.handleSetStateToCacheKey();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.location &&
            this.props.location &&
            prevProps.location.query.brand !== this.props.location.query.brand
        ) {
            // make linesheets panel search bar clear itself on brand change
            this.handleSearchChange(undefined, "");
        }
    }

    handleSetStateToCacheKey = () => {
        const __search = this.props.cacheKey.split("::")[1];
        let searchTerm;
        try {
            searchTerm = JSON.parse(__search).__search;
        } catch (e) {
            searchTerm = "";
        }
        this.setState({ searchTerm });
    };

    handleProductFilterChange = (filters) => {
        this.props.onChange(filters);
    };

    handleThrottleSearchChange = (searchTerm) => {
        const { filters } = this.props;
        this.props.onChange(searchTerm ? filters.set("__search", searchTerm) : filters.delete("__search"));
    };

    handleSearchChange = (e, searchTerm) => {
        this.handleThrottleSearchChange(searchTerm);
        this.setState({ searchTerm });
    };

    render() {
        const {
            className,
            linesheetsSort,
            linesheetsSortOrder,
            onSortChange,
            resource,
            viewport,
            onAddAllItems,
            bulkAddEnabled,
            onResetToDefaultShopView,
            onConfigureShopView,
            selectedShopViewKey,
        } = this.props;
        const { searchTerm } = this.state;

        let placeholder;

        if (resource === "item" || resource === "global-search") {
            placeholder = formatMessage("Search Products");
        } else if (resource === "brand") {
            placeholder = formatMessage("Search Brands");
        } else if (resource === "linesheets") {
            placeholder = formatMessage("Search Linesheets");
        }

        return (
            <header {...this.styles} className={className}>
                <Form>
                    <TextField
                        className="searchContainer"
                        dataTest={DATATEST.ASSORTMENTS.BRAND_SEARCH_INPUT}
                        canClear={true}
                        large={false}
                        fullWidth={true}
                        name="search"
                        placeholder={placeholder}
                        autoComplete="off"
                        style={this.muiStyles.textField}
                        value={searchTerm}
                        onChange={this.handleSearchChange}
                    />
                </Form>
                {onAddAllItems && (
                    <CatalogActions
                        onAddAllItems={onAddAllItems}
                        style={this.muiStyles.linesheetActions}
                        disabled={!bulkAddEnabled}
                        bulkAddingItems={false}
                    />
                )}
                {resource === "linesheets" && (
                    <div className="sort">
                        <Sort
                            choices={this.sortChoices}
                            onChange={onSortChange}
                            useNewIcon={true}
                            value={`${linesheetsSort}.${linesheetsSortOrder}`}
                            viewport={viewport}
                        />
                    </div>
                )}
            </header>
        );
    }
}
